export interface StatOption { id: number; text: string; }
export interface StatEntry { id: string; text: string; options?: StatOption[]; }
interface StatIndex {
  categories: Record<string, StatEntry[]>;
}

let STAT_INDEX: StatIndex | null = null;

export async function loadStatIndex(): Promise<void> {
  if (STAT_INDEX) return;
  const resp = await fetch('/stat-index.json');
  if (!resp.ok) throw new Error('Failed to load stat index.');
  STAT_INDEX = await resp.json();
}

export function getStatIndex(): StatIndex {
  if (!STAT_INDEX) throw new Error('Stat index not loaded.');
  return STAT_INDEX;
}

export function normalize(text: string): string {
  const t = text.replace(/([+\-]?)\d+(?:\.\d+)?/g, (_m, s) => (s || '') + '#');
  // Collapse spaces/tabs but preserve newlines (many stats are multi-line, e.g. keystone jewels)
  return t.replace(/[ \t]+/g, ' ').trim();
}

export function extractValue(text: string): number | null {
  const m = text.match(/\+?\(?(-?\d+(?:\.\d+)?)(?:\s*-\s*(-?\d+(?:\.\d+)?))?\)?/);
  if (!m) return null;
  return parseFloat(m[1]);
}

/** Extract a damage range "Adds X to Y" — returns {lo, hi, avg} or null. */
export function extractDamageRange(text: string): { lo: number; hi: number; avg: number } | null {
  const m = text.match(/Adds\s+(\d+(?:\.\d+)?)\s+to\s+(\d+(?:\.\d+)?)\s+/i);
  if (!m) return null;
  const lo = parseFloat(m[1]);
  const hi = parseFloat(m[2]);
  return { lo, hi, avg: (lo + hi) / 2 };
}

/** True if the stat-index entry text is a damage-range pattern ("Adds # to # ... Damage"). */
export function isDamageRangeStat(entryText: string): boolean {
  return /^Adds # to #\s+.+\bDamage\b/i.test(entryText);
}

/** Try to match a mod text to a stat entry. Returns the matched entry + category + any captured option text. */
export interface StatMatch { entry: StatEntry; category: string; capturedOption?: string; }

export function findStat(
  modText: string,
  prefer: string[] = ['explicit', 'implicit', 'crafted', 'fractured', 'enchant']
): StatMatch | null {
  const norm = normalize(modText);
  const raw = modText.replace(/[ \t]+/g, ' ').trim();
  // Raw (unnormalized) comes first — some cluster-jewel notable stats use literal "1" not "#"
  const variants = raw === norm ? [norm] : [raw, norm];

  // Fallback: (Local) suffix for armour mods
  if (!norm.includes('(Local)') && /increased (Armour|Evasion|Energy Shield|Armour and|Evasion and)/.test(norm)) {
    variants.push(norm + ' (Local)');
  }
  if (!norm.includes('(Local)') && /^\+# to (Armour|Evasion Rating|maximum Energy Shield)$/.test(norm)) {
    variants.push(norm + ' (Local)');
  }

  // Sign swap: "-# X" → "+# X"
  if (norm.startsWith('-#')) variants.push('+#' + norm.substring(2));
  if (norm.includes('applying -#%')) variants.push(norm.replace('applying -#%', 'applying #%'));

  // metre/metres plural
  if (norm.includes(' metres')) variants.push(norm.replace(' metres', ' metre'));
  if (norm.includes(' meters')) variants.push(norm.replace(' meters', ' metre'));

  // "+(#-#)" range → "+#"
  if (norm.includes('+(#-#)')) variants.push(norm.replace('+(#-#)', '+#'));
  if (norm.includes('(#-#)')) variants.push(norm.replace('(#-#)', '#'));

  // reduced/less → increased/more (inverse polarity, same stat ID)
  if (norm.includes(' reduced ')) variants.push(norm.replace(' reduced ', ' increased '));
  if (norm.includes(' less ')) variants.push(norm.replace(' less ', ' more '));
  if (norm.startsWith('reduced ')) variants.push('increased ' + norm.substring(8));

  // Plural Modifier(s)
  for (const plur of [' Modifiers ', ' Modifiers allowed']) {
    const sing = plur.replace('Modifiers', 'Modifier');
    if (norm.includes(plur)) variants.push(norm.replace(plur, sing));
    if (norm.includes(sing)) variants.push(norm.replace(sing, plur));
  }

  // Combine sign-flip + all other fallbacks
  const base = [...variants];
  for (const v of base) {
    if (v.startsWith('-#')) variants.push('+#' + v.substring(2));
  }

  // Fallback 7: loose article matching — strip redundant "the" that PoB sometimes inserts
  // e.g. "if you have the matching modifier" vs index "if you have matching modifier"
  const withoutThe = new Set<string>();
  for (const v of variants) {
    const stripped = v.replace(/\bthe\s+/g, '');
    if (stripped !== v) withoutThe.add(stripped);
  }
  variants.push(...withoutThe);

  // Fallback 8: singular→plural for cluster-jewel notable patterns
  // "# Added Passive Skill is a Jewel Socket" → "# Added Passive Skills are Jewel Sockets"
  const pluralized = new Set<string>();
  for (const v of variants) {
    const p = v
      .replace(/\bAdded Passive Skill is a Jewel Socket\b/g, 'Added Passive Skills are Jewel Sockets')
      .replace(/\bAdded Passive Skill is Small and Grants:/g, 'Added Small Passive Skills grant:');
    if (p !== v) pluralized.add(p);
  }
  variants.push(...pluralized);

  const idx = getStatIndex();
  const allCats = [...prefer, ...Object.keys(idx.categories).filter(c => !prefer.includes(c))];

  // Exact match first
  for (const cat of allCats) {
    const entries = idx.categories[cat] ?? [];
    for (const e of entries) {
      if (variants.includes(e.text)) return { entry: e, category: cat };
    }
  }

  // Case-insensitive
  const lower = new Set(variants.map(v => v.toLowerCase()));
  for (const cat of allCats) {
    const entries = idx.categories[cat] ?? [];
    for (const e of entries) {
      if (lower.has(e.text.toLowerCase())) return { entry: e, category: cat };
    }
  }

  // Option-placeholder match: if stat has options, treat `#` as wildcard and capture it.
  // e.g. "Passive Skills in Radius of # can be Allocated" matches "... of Minion Instability ..."
  // The captured fragment ("Minion Instability") is used to look up the option ID.
  for (const cat of allCats) {
    const entries = idx.categories[cat] ?? [];
    for (const e of entries) {
      if (!e.options || !e.options.length) continue;
      if (!e.text.includes('#')) continue;
      // Handle multi-line patterns: escape and replace # with wildcard (dotall needed for newlines)
      const escaped = e.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Replace # with a captured group that matches across lines
      const pattern = new RegExp('^' + escaped.replace(/#/g, '([\\s\\S]+?)') + '$');
      for (const v of variants) {
        const m = v.match(pattern);
        if (m) {
          const captured = m.slice(1).join(' | '); // If multiple #, join
          return { entry: e, category: cat, capturedOption: captured };
        }
      }
    }
  }

  return null;
}

/**
 * Multi-line match: try joining consecutive mod lines (1-4) starting at idx.
 * Returns {match, consumed} or null. Used for mods like Impossible Escape that span multiple lines.
 */
export function findStatMultiline(
  mods: string[],
  idx: number,
  prefer?: string[],
): { match: StatMatch; consumed: number; joinedText: string } | null {
  const maxLook = Math.min(4, mods.length - idx);
  // Try longest first (greedy)
  for (let n = maxLook; n >= 1; n--) {
    const slice = mods.slice(idx, idx + n);
    const joined = slice.map(m => m.replace(/\{[^}]*\}/g, '').trim()).join('\n');
    if (!joined) continue;
    const result = findStat(joined, prefer);
    if (result) return { match: result, consumed: n, joinedText: joined };
  }
  return null;
}

/**
 * For an options-based stat (e.g. Forbidden Flesh, Impossible Escape, Watcher's Eye),
 * extract the option ID. If `captured` is given (from the placeholder match), match that exactly first.
 * Otherwise fall back to substring-match within the full mod text.
 */
export function findOptionId(entry: StatEntry, modText: string, captured?: string): number | null {
  if (!entry.options || !entry.options.length) return null;
  const cleaned = modText.replace(/\{[^}]*\}/g, '').trim();

  // If we have a captured fragment, try exact text match against options first
  if (captured) {
    const cap = captured.trim();
    const exact = entry.options.find(o => o.text.trim() === cap);
    if (exact) return exact.id;
    // Try normalizing both sides (collapse whitespace, trim)
    const capNorm = cap.replace(/\s+/g, ' ');
    const norm = entry.options.find(o => o.text.replace(/\s+/g, ' ') === capNorm);
    if (norm) return norm.id;
  }

  // Fall back to longest substring match
  const sorted = [...entry.options].sort((a, b) => b.text.length - a.text.length);
  for (const opt of sorted) {
    if (cleaned.includes(opt.text)) return opt.id;
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════
// Pseudo-Aggregation (Awakened PoE Trade style)
// ═══════════════════════════════════════════════════════════════════

export const PSEUDOS: Record<string, string> = {
  life: 'pseudo.pseudo_total_life',
  es: 'pseudo.pseudo_total_energy_shield',
  str: 'pseudo.pseudo_total_strength',
  dex: 'pseudo.pseudo_total_dexterity',
  int: 'pseudo.pseudo_total_intelligence',
  fire_res: 'pseudo.pseudo_total_fire_resistance',
  cold_res: 'pseudo.pseudo_total_cold_resistance',
  lightning_res: 'pseudo.pseudo_total_lightning_resistance',
  chaos_res: 'pseudo.pseudo_total_chaos_resistance',
  elemental_res: 'pseudo.pseudo_total_elemental_resistance',
  life_regen: 'pseudo.pseudo_total_life_regen',
  mana: 'pseudo.pseudo_total_mana',
  all_attr: 'pseudo.pseudo_total_all_attributes',
};

export function aggregatePseudos(mods: string[]): { totals: Record<string, number>; unmapped: string[] } {
  const totals: Record<string, number> = {};
  const unmapped: string[] = [];
  const add = (k: string, v: number) => { totals[k] = (totals[k] ?? 0) + v; };

  for (const raw of mods) {
    const cleaned = raw.replace(/\{[^}]*\}/g, '').trim();
    if (!cleaned) continue;
    if (cleaned.startsWith('Quality') || cleaned.startsWith('Sockets')) {
      unmapped.push(cleaned); continue;
    }

    // Cluster-jewel passive grants are conditional (granted by added passives, not directly rolled)
    // → don't aggregate into pseudo totals; let them match as their own cluster stats
    if (cleaned.startsWith('Added Small Passive Skills') ||
        cleaned.startsWith('Added Passive Skills') ||
        /^\d+ Added Passive Skill\b/.test(cleaned) ||
        cleaned.startsWith('Adds ') && cleaned.endsWith('Passive Skills')) {
      unmapped.push(cleaned); continue;
    }

    // Conditional mods (Watcher's Eye "while affected by X", "during any Flask Effect",
    // "when on Low Life", "per Endurance Charge", etc.) have their own specific stat IDs
    // and must NOT be folded into the unconditional pseudo totals.
    if (/ (while|during) /i.test(cleaned) ||
        /\bwhen on (Low|Full) Life\b/i.test(cleaned) ||
        /\bper (Endurance|Frenzy|Power) Charge\b/i.test(cleaned) ||
        /\bper Poison on you\b/i.test(cleaned)) {
      unmapped.push(cleaned); continue;
    }

    let matched = false;
    let m: RegExpMatchArray | null;

    if ((m = cleaned.match(/\+(\d+)\s+to\s+maximum\s+Life\b/))) {
      add('life', parseInt(m[1], 10)); matched = true;
    }
    if ((m = cleaned.match(/Regenerate\s+(\d+(?:\.\d+)?)\s+Life\s+per\s+second/))) {
      add('life_regen', parseFloat(m[1])); matched = true;
    }
    if ((m = cleaned.match(/\+(\d+)\s+to\s+maximum\s+Energy\s+Shield\b/))) {
      add('es', parseInt(m[1], 10)); matched = true;
    }
    if ((m = cleaned.match(/\+(\d+)\s+to\s+maximum\s+Mana\b/))) {
      add('mana', parseInt(m[1], 10)); matched = true;
    }

    if ((m = cleaned.match(/\+(\d+)\s+to\s+all\s+Attributes/))) {
      const v = parseInt(m[1], 10);
      add('str', v); add('dex', v); add('int', v); add('all_attr', v);
      matched = true;
    }

    const rangeDual = cleaned.match(/\+\((\d+)\s*-\s*(\d+)\)\s+to\s+(Strength|Dexterity|Intelligence)\s+and\s+(Strength|Dexterity|Intelligence)/);
    if (rangeDual) {
      const v = parseInt(rangeDual[1], 10);
      for (const attr of [rangeDual[3], rangeDual[4]]) {
        add(attr.toLowerCase().slice(0, 3), v);
      }
      matched = true;
    }

    const dualAttr = cleaned.match(/\+(\d+)\s+to\s+(Strength|Dexterity|Intelligence)\s+and\s+(Strength|Dexterity|Intelligence)/);
    if (!matched && dualAttr) {
      const v = parseInt(dualAttr[1], 10);
      for (const attr of [dualAttr[2], dualAttr[3]]) {
        add(attr.toLowerCase().slice(0, 3), v);
      }
      matched = true;
    }

    if (!matched) {
      for (const [attr, pk] of [['Strength', 'str'], ['Dexterity', 'dex'], ['Intelligence', 'int']]) {
        const re = new RegExp(`\\+(\\d+)\\s+to\\s+${attr}\\b`);
        const mm = cleaned.match(re);
        if (mm) { add(pk, parseInt(mm[1], 10)); matched = true; break; }
      }
    }

    if ((m = cleaned.match(/\+(\d+)%\s+to\s+all\s+Elemental\s+Resistances/))) {
      const v = parseInt(m[1], 10);
      add('fire_res', v); add('cold_res', v); add('lightning_res', v); add('elemental_res', v);
      matched = true;
    }

    for (const [ele, pk] of [['Fire', 'fire_res'], ['Cold', 'cold_res'], ['Lightning', 'lightning_res'], ['Chaos', 'chaos_res']]) {
      const re = new RegExp(`\\+(\\d+)%\\s+to\\s+${ele}\\s+Resistance(?!s)`);
      const mm = cleaned.match(re);
      if (mm) {
        const v = parseInt(mm[1], 10);
        add(pk, v);
        if (ele !== 'Chaos') add('elemental_res', v);
        matched = true;
      }
    }

    const dualRes = cleaned.match(/\+(\d+)%\s+to\s+(Fire|Cold|Lightning)\s+and\s+(Fire|Cold|Lightning)\s+Resistances/);
    if (dualRes) {
      const v = parseInt(dualRes[1], 10);
      const map: Record<string, string> = { Fire: 'fire_res', Cold: 'cold_res', Lightning: 'lightning_res' };
      for (const e of [dualRes[2], dualRes[3]]) {
        add(map[e], v); add('elemental_res', v);
      }
      matched = true;
    }

    if (!matched) unmapped.push(cleaned);
  }

  return { totals, unmapped };
}

export function hasInversePolarity(text: string): boolean {
  return /\b(reduced|less)\b/.test(text);
}
